import type { SourcesView, StepView } from '@/types/pipeline';

/**
 * The build view's three steps, derived from pipeline-status alone.
 *
 * They are strictly in order, so at most one is ever active:
 *   1. reading the sources (or gathering them for a topic-only lesson),
 *   2. planning the lesson - the plan call (`byte_selection` stage `outline`,
 *      or queued once the sources are read),
 *   3. building the lesson - each concept's details written, then the save
 *      (`details` / `saving`), while a draft map already exists.
 *
 * The overview (title + summary) is NOT a step: it runs beside step 1 and
 * nothing waits for it, so it is shown as the lesson's title instead
 * (`titleState`).
 */

export type StageStatus = 'pending' | 'active' | 'done';

export type StageKey = 'sources' | 'outline' | 'build';

export interface BuildStage {
  key: StageKey;
  /** The stepper's short label. */
  label: string;
  /** The heading while this step is active. */
  heading: string;
  hint: string;
  /** How long this step usually takes (measured), shown while active. */
  estimate: string;
  status: StageStatus;
}

/** What the stage card shows: tips, the read material, or the draft map. */
export type BuildPanelKind = 'cards' | 'preview' | 'outline';

export interface BuildStagesInput {
  sources?: SourcesView;
  byteSelection?: StepView;
  hasUserSources?: boolean;
}

export interface BuildStagesResult {
  stages: BuildStage[];
  /** The active step (the last one once everything is done). */
  current: BuildStage;
  panel: BuildPanelKind;
}

/** The title is being written / is ready / could not be written. */
export type TitleState = 'writing' | 'ready' | 'failed';

export function titleState(overview?: StepView): TitleState {
  if (overview?.state === 'failed') return 'failed';
  return overview?.state === 'completed' ? 'ready' : 'writing';
}

/** Stages past the outline call, when a draft mind map exists. `checking`
 *  is the name before plan v3, kept for a lesson planned across a deploy. */
const LATE_STAGES = new Set(['details', 'checking', 'saving']);

/** `byte_selection` is past the outline call: a draft mind map exists. */
export function isLateBuildStage(byteSelection?: StepView): boolean {
  return (
    byteSelection?.state === 'processing' &&
    LATE_STAGES.has(byteSelection.stage ?? '')
  );
}

function outlineStatus(
  sourcesDone: boolean,
  byteSelection?: StepView
): StageStatus {
  const state = byteSelection?.state;
  if (state === 'completed' || isLateBuildStage(byteSelection)) return 'done';
  return sourcesDone ? 'active' : 'pending';
}

function buildStatus(byteSelection?: StepView): StageStatus {
  if (byteSelection?.state === 'completed') return 'done';
  return isLateBuildStage(byteSelection) ? 'active' : 'pending';
}

const PANEL_FOR: Record<StageKey, BuildPanelKind> = {
  sources: 'cards',
  outline: 'preview',
  build: 'outline',
};

export function deriveBuildStages({
  sources,
  byteSelection,
  hasUserSources,
}: BuildStagesInput): BuildStagesResult {
  // A plan already under way (or done) was made from material that was read:
  // a source re-processed meanwhile must not set the first step spinning
  // again next to it.
  const planStarted =
    byteSelection?.state === 'processing' ||
    byteSelection?.state === 'completed';
  const sourcesDone = sources?.overall === 'completed' || planStarted;
  const stages: BuildStage[] = [
    {
      key: 'sources',
      label: hasUserSources ? 'Reading your material' : 'Gathering material',
      heading: hasUserSources
        ? 'Reading your material'
        : 'Gathering study material',
      hint: hasUserSources
        ? 'Going through your sources section by section.'
        : 'Finding trusted material on your topic.',
      estimate: 'A few seconds',
      status: sourcesDone ? 'done' : 'active',
    },
    {
      key: 'outline',
      label: 'Planning your lesson',
      heading: 'Planning your lesson',
      hint: 'Turning the material into sections and concepts. This becomes your mind map.',
      estimate: 'About 1–2 minutes',
      status: outlineStatus(sourcesDone, byteSelection),
    },
    {
      key: 'build',
      label: 'Building your lesson',
      heading: 'Building your lesson',
      hint: 'Writing up each concept, then opening your mind map.',
      estimate: 'A few seconds',
      status: buildStatus(byteSelection),
    },
  ];
  const current =
    stages.find(s => s.status === 'active') ?? stages[stages.length - 1];
  return { stages, current, panel: PANEL_FOR[current.key] };
}
